import {
  OrgId,
  wasOrgCreatedAfterMagicTermsPartOfServiceAgreementDate,
} from "@hex/common";

import {
  useGetActiveDocusignEnvelopeForEnvelopeTypeQuery,
  useGetOrgForMagicTosQuery,
} from "./magic-2025-tos-dialog/hooks.generated";

/**
 * Returns whether the org has signed the Magic Output Data Addendum, if required.
 * This includes whether the envelope completion was overridden via envelopeCompletionOverrides,
 * or the envelope exists in a completed status in the DocusignEnvelope table.
 *
 * Note: consumers _must_ check the loading state before using this hook, as it requires
 * queries to be run, and will return false on initial load even if the org has signed the addendum.
 *
 * All this logic should be able to be removed at the end of July after we force all customers
 * to sign the addendum or turn off magic.
 */
export const useHasSignedMagicOutputDataAddendumIfRequired = (
  orgId: OrgId,
): {
  isSigned: boolean;
  isLoading: boolean;
} => {
  const { data: orgData, loading: orgLoading } = useGetOrgForMagicTosQuery({
    variables: { orgId },
  });
  const org = orgData?.orgById;

  const { data: envelopeData, loading: envelopeLoading } =
    useGetActiveDocusignEnvelopeForEnvelopeTypeQuery({
      variables: {
        envelopeType: "MAGIC_OUTPUT_DATA_ADDENDUM",
        orgId,
      },
    });

  const isSelfServe = org?.isSelfServe;
  const orgCreatedAfterFeb2025 = org?.createdDate
    ? wasOrgCreatedAfterMagicTermsPartOfServiceAgreementDate(org?.createdDate)
    : false;
  const hasEnvelopeCompletionOverride =
    org?.envelopeCompletionOverrides?.["MAGIC_OUTPUT_DATA_ADDENDUM"] != null;
  const hasCompletedEnvelope =
    envelopeData?.getActiveDocusignEnvelopeForEnvelopeType?.envelopeStatus ===
    "completed";

  const orgHasSignedAddendum =
    isSelfServe ||
    orgCreatedAfterFeb2025 ||
    hasEnvelopeCompletionOverride ||
    hasCompletedEnvelope;

  return {
    isSigned: orgHasSignedAddendum,
    isLoading: orgLoading || envelopeLoading,
  };
};
